import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
} from "@mui/material";
import { Delete, ExpandMore, Lock, LockOpen, MusicNote, Videocam } from "@mui/icons-material";
import Swal from "sweetalert2";
import AudioContainerPreview from "../../Common/AudioContainerPreview";
import {
  addEditBGM,
  fetchOneBGMWithRelatedContent,
  deleteBGM,
} from "../../../actions/backgroundMusicActions";
import {
  uploadAudio,
  clearMedia,
} from "../../../actions/mediaActions";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import {
  shouldConfirmReplacement,
  replacementWarningHtml,
} from "../../../utils/backgroundMusicReplacement";

const AddEditBackgroundMusic = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [state, setState] = useState({
    title: "",
    audio_url: "",
    // The file this track had when the form loaded. Replacing it now changes
    // what every locked session plays, so the save path compares against it.
    original_audio_url: "",
    audio_preview: null,
    audio_length: 0,
    isSetToEdit: false,
  });

  const [relatedContent, setRelatedContent] = useState({
    sessions: [],
    quick_taps: [],
    total_usage: 0,
    loading: false,
  });

  // Synchronous re-entrancy lock. The confirmation below awaits, so without
  // this a second Save click while the dialog is open opens a second dialog
  // and can dispatch the save twice.
  const isSubmittingRef = useRef(false);

  const isFetching = useSelector((state) => state.background_musics.isFetching);
  const audio = useSelector((state) => state.media.audio);

  // Fetch background music and related content when editing
  useEffect(() => {
    if (!isEdit || !selectedId) return;

    const fetchBackgroundMusicData = async () => {
      setRelatedContent(prev => ({ ...prev, loading: true }));

      try {
        const bgmData = await dispatch(fetchOneBGMWithRelatedContent(selectedId));
        if (bgmData && bgmData.bgm_id) {

          // Set background music form data (now flattened at top level of data)
          setState({
            title: bgmData.bgm_title,
            audio_url: bgmData.bgm_url,
            original_audio_url: bgmData.bgm_url,
            audio_preview: bgmData.bgm_url,
            audio_length: bgmData.bgm_audio_length || 0,
            isSetToEdit: true,
          });

          // Set related content data
          setRelatedContent({
            sessions: bgmData.sessions || [],
            quick_taps: bgmData.quick_taps || [],
            total_usage: bgmData.total_usage || 0,
            loading: false,
          });
        }
      } catch (error) {
        console.error("Error fetching background music data:", error);
        setRelatedContent(prev => ({ ...prev, loading: false }));
      }
    };

    fetchBackgroundMusicData();
  }, [dispatch, isEdit, selectedId]);

  // Handle audio upload
  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await dispatch(uploadAudio(file));
      } catch (error) {
        console.error("Error uploading audio:", error);
        Swal.fire("Error", "Failed to upload audio file", "error");
      }
    }
  };

  // Update state when audio is uploaded
  useEffect(() => {
    if (audio) {
      setState((prev) => ({
        ...prev,
        audio_url: audio,
        audio_preview: audio,
      }));
    }
  }, [audio]);

  const handleAudioDuration = (e) => {
    const duration = Math.floor(e.target.duration) || 0;
    setState(prev => ({
      ...prev,
      audio_length: Math.round(duration)
    }));
  };

  const submit = useCallback(async () => {
    if (isSubmittingRef.current) return;

    if (!state.title) {
      Swal.fire("Error", "Please enter a title", "error");
      return;
    }

    if (!state.audio_url) {
      Swal.fire("Error", "Please upload an audio file", "error");
      return;
    }

    if (!state.audio_length) {
      Swal.fire("Error", "Please wait for the audio to load", "error");
      return;
    }

    isSubmittingRef.current = true;

    try {
      if (
        shouldConfirmReplacement({
          isEdit,
          originalUrl: state.original_audio_url,
          nextUrl: state.audio_url,
          relatedContent,
        })
      ) {
        const result = await Swal.fire({
          title: "Replace this music file?",
          html: replacementWarningHtml(relatedContent),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, replace it",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;
      }

      const backgroundMusicData = {
        bgm_title: state.title,
        bgm_url: state.audio_url,
        bgm_audio_length: state.audio_length,
        ...(isEdit && { bgm_id: selectedId })
      };

      dispatch(addEditBGM({
        bgm_json: JSON.stringify(backgroundMusicData)
      }, () => navigate('/background-musics')));
      dispatch(clearMedia());
    } catch (error) {
      console.error("Error saving background music:", error);
      Swal.fire("Error", "Failed to save background music", "error");
    } finally {
      isSubmittingRef.current = false;
    }
  }, [state, isEdit, selectedId, dispatch, navigate, relatedContent]);

  const handleRemoveAudio = () => {
    setState((prev) => ({
      ...prev,
      audio_url: "",
      audio_preview: null,
      audio_length: 0,
    }));
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteBGM(selectedId, () => navigate("/background-musics")));
      }
    });
  };

  useImperativeHandle(ref, () => ({
    submit
  }), [submit]);

  if (isEdit && !state.isSetToEdit && !relatedContent.loading) {
    return <LoadingBackdrop open={true} />;
  }

  return (
    <Box component="form" autoComplete="off" sx={{ width: '100%' }}>
      <Grid container spacing={3} sx={{ width: '100%', m: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
        <Grid sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Background Music Info</Typography>
          <TextField
            fullWidth
            label="Title"
            value={state.title}
            onChange={(e) => setState((prev) => ({ ...prev, title: e.target.value }))}
            required
          />
        </Grid>
        <Grid sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Background Music Audio</Typography>
          <AudioContainerPreview
            title="Background Music Audio"
            audio={state.audio_preview}
            name="audio"
            handleRemove={handleRemoveAudio}
            handleAudioUpload={handleAudioUpload}
            showCopyUrlButton={true}
            onLoadedData={handleAudioDuration}
          />
        </Grid>
      </Grid>
      {isEdit && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="error"
            onClick={handleDelete}
            startIcon={<Delete />}
            variant="contained"
          >
            Delete
          </Button>
        </Box>
      )}

      {/* Related Content Section */}
      {isEdit && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MusicNote />
            Content Using This Background Music
            {relatedContent.total_usage > 0 && (
              <Chip
                label={`${relatedContent.total_usage} items`}
                color="primary"
                size="small"
              />
            )}
          </Typography>

          {relatedContent.loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">Loading related content...</Typography>
            </Box>
          ) : relatedContent.total_usage === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography color="text.secondary">
                This background music is not currently used by any sessions or quick taps.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Sessions Section */}
              {relatedContent.sessions.length > 0 && (
                <Accordion defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MusicNote color="primary" fontSize="small" />
                      Audio Sessions ({relatedContent.sessions.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {relatedContent.sessions.map((session, index) => (
                        <ListItem key={session.id || index} divider>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <MusicNote color="primary" fontSize="small" />
                              {session.is_music_locked === 1 ? (
                                <Lock color="error" sx={{ fontSize: 14 }} />
                              ) : (
                                <LockOpen color="success" sx={{ fontSize: 14 }} />
                              )}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1">{session.title}</Typography>
                                <Chip
                                  label="Audio Session"
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={session.parent_type}
                                  size="small"
                                  variant="outlined"
                                />
                                {session.parent_name && (
                                  <Typography variant="body2" color="text.secondary">
                                    {session.parent_name}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Quick Taps Section */}
              {relatedContent.quick_taps.length > 0 && (
                <Accordion defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Videocam color="secondary" fontSize="small" />
                      Video Quick Taps ({relatedContent.quick_taps.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {relatedContent.quick_taps.map((quickTap, index) => (
                        <ListItem key={quickTap.id || index} divider>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Videocam color="secondary" fontSize="small" />
                              {quickTap.is_music_locked === 1 ? (
                                <Lock color="error" sx={{ fontSize: 14 }} />
                              ) : (
                                <LockOpen color="success" sx={{ fontSize: 14 }} />
                              )}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1">{quickTap.title}</Typography>
                                <Chip
                                  label="Video Quick Tap"
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={quickTap.parent_type}
                                  size="small"
                                  variant="outlined"
                                />
                                {quickTap.parent_name && (
                                  <Typography variant="body2" color="text.secondary">
                                    {quickTap.parent_name}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </Box>
      )}

      <LoadingBackdrop open={isFetching || false} />
    </Box>
  );
});

AddEditBackgroundMusic.displayName = 'AddEditBackgroundMusic';

export default AddEditBackgroundMusic;
